import { useEffect, useRef, type ReactNode } from 'react';
import { Container } from 'react-bootstrap';
import { useLocation, useNavigationType } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppNavbar } from './AppNavbar';
import { AppFooter } from './AppFooter';

export function AppShell({ children }: { children: ReactNode }) {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const navType = useNavigationType();
    const mainRef = useRef<HTMLElement>(null);
    const firstRender = useRef(true);

    // Move focus to the main landmark on SPA navigation so keyboard and
    // screen-reader users land at the new page content instead of staying on
    // the link they clicked.
    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }
        // On forward (PUSH/REPLACE) navigations, start at the top. On POP
        // (back/forward) leave the scroll position alone so the browser can
        // restore where the user was. Either way move focus to the main landmark
        // without letting focus() scroll the page (the tabbed mappings screen
        // otherwise nudges it down a little).
        if (navType !== 'POP') window.scrollTo(0, 0);
        mainRef.current?.focus({ preventScroll: true });
    }, [pathname, navType]);

    return (
        <div className="app-shell">
            <a href="#main-content" className="visually-hidden-focusable skip-link">
                {t('a11y.skipToContent')}
            </a>
            <AppNavbar />
            <main id="main-content" tabIndex={-1} ref={mainRef} className="app-shell__main">
                <Container>{children}</Container>
            </main>
            <AppFooter />
        </div>
    );
}
